import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { TodoApi } from "../api";
import { useAuth } from "./authProvider";
import { nextStepOrder } from "./stepOrder";
import { dueRepeatResets, isRepeatDue, REPEAT_RESET_CONCURRENCY, repeatResetUpdates, runLimited } from "../repeat";
import { TodoData, CreateTodoInput, TodoRepeat } from "../../../types";

type TodoUpdates = Omit<
  Partial<TodoData>,
  "scheduledDate" | "dueDate" | "priority" | "complexity" | "parentId" | "repeat"
> & {
  // null clears the field (e.g. un-scheduling from Today, clearing a
  // priority/complexity you'd previously set, or un-nesting a step back to
  // the Inbox) — see formatUpdateRequest on the backend for why this needs
  // its own sentinel.
  scheduledDate?: string | null;
  dueDate?: string | null;
  priority?: number | null;
  complexity?: number | null;
  parentId?: string | null;
  repeat?: TodoRepeat | null;
  // Sent with a non-null parentId so the server can read the new parent (its
  // key needs createdAt) and refuse a missing or nested one. Wire-only: it is
  // not a field of the todo and never lands on the local copy.
  parentCreatedAt?: string;
};

const dataContext = createContext<{
  todos: TodoData[];
  loading: boolean;
  createTodo: (input: Omit<CreateTodoInput, "id">) => Promise<void>;
  // Resolves true once the server saved the change, false when it was undone
  // (the banner below says so), so a caller doing several edits in a row can
  // stop at the first failure.
  updateTodo: (todo: TodoData, updates: TodoUpdates) => Promise<boolean>;
  deleteTodo: (todo: TodoData) => Promise<void>;
}>({
  todos: [],
  loading: true,
  createTodo: async () => {},
  updateTodo: async () => false,
  deleteTodo: async () => {},
});

const LOAD_ERROR = "Couldn't load your todos.";
const SAVE_ERROR = "Couldn't save that change, so it was undone.";
const REPEAT_RESET_ERROR = "Couldn't bring back a repeating todo; it will retry next time you open the app.";

const nowISO = () => new Date().toISOString();

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
  // The owner of every todo in this list (Router only mounts DataProvider
  // once logged in, and logout unmounts it). Only used to keep the repeat
  // reset to the user's own todos.
  const { auth } = useAuth();
  const userId = auth.status === "loggedIn" ? auth.userId : undefined;
  const [todos, setTodos] = useState<TodoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [updateError, setUpdateError] = useState<string | null>(null);
  // Source of truth for mutations: every change goes through `mutate`, which
  // updates the ref synchronously and then mirrors it into state. The ref is
  // never written during render, so it can't lag or misbehave under
  // concurrent rendering, and async handlers always read the current list.
  const todosRef = useRef<TodoData[]>([]);
  const mutate = useCallback((fn: (list: TodoData[]) => TodoData[]) => {
    todosRef.current = fn(todosRef.current);
    setTodos(todosRef.current);
  }, []);

  // In-flight edits per `${todoId}:${field}` (see updateTodo).
  const fieldEditsRef = useRef(
    new Map<string, { base: unknown; inflight: number; seq: number; okSeq: number; okValue: unknown }>(),
  );

  // Ids deleted locally while at least one load() is in flight. A response can
  // still contain them (e.g. created, then deleted mid-fetch), so every
  // overlapping load merges against this one shared set; it is emptied when
  // the last pending load settles.
  const pendingLoadsRef = useRef(0);
  const tombstonesRef = useRef(new Set<string>());
  // Ids whose failed delete was rolled back while a load is in flight. A stale
  // response may lack them, but they still exist, so the merge keeps them.
  // Emptied alongside the tombstones.
  const restoredRef = useRef(new Set<string>());
  // Start order of loads, and the newest start order merged so far. A slower,
  // older response arriving after a newer one has merged is stale, so skip it.
  const loadSeqRef = useRef(0);
  const mergedSeqRef = useRef(0);

  // Repeating todos that have come due are unchecked once per mount, right
  // after the first successful load merges (a failed first load leaves this
  // unset, so the user's Retry still gets one pass). Each uncheck is an
  // ordinary optimistic updateTodo, at most REPEAT_RESET_CONCURRENCY in
  // flight: the first few land in the same commit as the merge and the rest
  // follow as slots free up. Loading never waits on them, a failure rolls
  // back to done via the usual path with its own banner and is logged, and
  // nothing retries or refetches. A queued todo is re-checked against the
  // current list when its turn comes, so one the user deleted, unchecked or
  // re-completed meanwhile is skipped, and the queue stops on unmount. After
  // an uncheck `done` is false, so the next load does not pick the todo up
  // again even though doneAt stays. A "start" mode repeat also writes its
  // moved-forward anchor (repeatResetUpdates), which is what keeps it from
  // firing again; it is skipped while the user's own repeat edit is in
  // flight, since the reset's copy of the repeat would be stale.
  // updateTodo is a plain function defined below; the ref carries its latest
  // copy (saveTodo, so the reset can pass its own banner) and is assigned in
  // an effect (never during render) declared before the load effect, and
  // load only needs it once its fetch has resolved.
  const repeatResetDoneRef = useRef(false);
  const mountedRef = useRef(false);
  const updateTodoRef = useRef<
    (todo: TodoData, updates: TodoUpdates, failMessage?: string) => Promise<boolean>
  >(async () => false);
  const resetDueRepeats = useCallback((ownerId: string) => {
    if (repeatResetDoneRef.current) return;
    repeatResetDoneRef.current = true;
    const now = new Date();
    void runLimited(
      dueRepeatResets(todosRef.current, now, ownerId),
      REPEAT_RESET_CONCURRENCY,
      async (queued) => {
        const todo = todosRef.current.find((t) => t.id === queued.id);
        if (!todo || todo.userId !== ownerId || !isRepeatDue(todo, now)) return;
        if (fieldEditsRef.current.has(`${todo.id}:repeat`)) return;
        try {
          const ok = await updateTodoRef.current(todo, repeatResetUpdates(todo, now), REPEAT_RESET_ERROR);
          if (!ok) console.error("Couldn't reset repeating todo", todo.id);
        } catch (err) {
          console.error("Couldn't reset repeating todo", todo.id, err);
        }
      },
      () => !mountedRef.current,
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    pendingLoadsRef.current++;
    const seq = ++loadSeqRef.current;
    // Local changes made while the fetch is in flight must survive it. Objects
    // are only replaced by `mutate` when changed, so comparing against this
    // snapshot by reference tells us what the user touched in the meantime.
    const before = todosRef.current;
    try {
      let result;
      try {
        result = await TodoApi.getAll();
      } catch (err) {
        console.error("Failed to load todos", err);
        result = undefined;
      }
      const tombstones = tombstonesRef.current;
      const restored = restoredRef.current;
      if (!result?.success || !result.data) {
        // Leave current state alone; retrying is user-triggered only.
        if (result && !result.success) console.error("Failed to load todos", result);
        setUpdateError(LOAD_ERROR);
      } else {
        // A successful (re)load clears a previous load-failure banner only.
        setUpdateError((e) => (e === LOAD_ERROR ? null : e));
        const fetched = result.data.todos;
        if (seq > mergedSeqRef.current) {
          mergedSeqRef.current = seq;
          mutate((current) => {
            const beforeById = new Map(before.map((t) => [t.id, t]));
            const currentById = new Map(current.map((t) => [t.id, t]));
            const merged: TodoData[] = [];
            const seen = new Set<string>();
            const takenFromServer = new Map<string, TodoData>();
            for (const server of fetched) {
              seen.add(server.id);
              const prior = beforeById.get(server.id);
              const local = currentById.get(server.id);
              if (tombstones.has(server.id)) continue; // deleted locally while a load was in flight
              if (prior && !local) continue; // deleted locally during the fetch
              const useServer = !(local && local !== prior);
              merged.push(useServer ? server : local);
              if (useServer) takenFromServer.set(server.id, server);
            }
            // Created locally during the fetch and not in the response yet.
            for (const local of current) {
              if (!seen.has(local.id) && !beforeById.has(local.id)) merged.push(local);
            }
            // Restored by a failed delete but missing from a stale response:
            // keep it, placed before its first surviving successor in `current`.
            current.forEach((local, i) => {
              if (seen.has(local.id) || !restored.has(local.id)) return;
              const later = new Set(current.slice(i + 1).map((t) => t.id));
              const at = merged.findIndex((t) => later.has(t.id));
              merged.splice(at === -1 ? merged.length : at, 0, local);
            });
            // An edit still in flight when the reload landed must not roll the
            // field back to its pre-reload value if it fails later: re-base it
            // on what the reload says.
            for (const [mapKey, edit] of Array.from(fieldEditsRef.current)) {
              const sep = mapKey.indexOf(":");
              const server = takenFromServer.get(mapKey.slice(0, sep));
              if (server) edit.base = server[mapKey.slice(sep + 1) as keyof TodoData];
            }
            return merged;
          });
          if (userId) resetDueRepeats(userId);
        }
      }
    } finally {
      if (--pendingLoadsRef.current === 0) {
        tombstonesRef.current.clear();
        restoredRef.current.clear();
      }
      setLoading(false);
    }
  }, [mutate, resetDueRepeats, userId]);

  useEffect(() => {
    updateTodoRef.current = saveTodo;
  });
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // Open-todo count in the tab title, e.g. "(3) todoit". Derived from state,
  // so it follows creates, completions and deletes without extra fetching.
  const openCount = todos.filter((t) => !t.done).length;
  useEffect(() => {
    document.title = openCount > 0 ? `(${openCount}) todoit` : "todoit";
  }, [openCount]);

  // Optimistic: the client generates the id and renders immediately, then
  // reconciles with whatever the server actually stored (server sets
  // createdAt/updatedAt, which is the source of truth for the sort key).
  const createTodo = async (input: Omit<CreateTodoInput, "id">) => {
    const id = crypto.randomUUID();
    const optimistic: TodoData = {
      type: "todo",
      id,
      userId: "",
      title: input.title,
      notes: input.notes,
      context: input.context,
      parentId: input.parentId,
      priority: input.priority,
      complexity: input.complexity,
      done: false,
      order: input.parentId ? nextStepOrder() : -Date.now(), // steps go last; top-level newest first
      timesScheduled: input.scheduledDate ? 1 : 0,
      scheduledDate: input.scheduledDate,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    };
    mutate((t) => [...t, optimistic]);
    let result: Awaited<ReturnType<typeof TodoApi.createTodo>> | undefined;
    try {
      result = await TodoApi.createTodo({ ...input, id });
    } catch {
      result = undefined;
    }
    if (result?.success && result.data) {
      mutate((t) => t.map((existing) => (existing.id === id ? result.data : existing)));
    } else {
      // Remove the ghost todo; only this id is touched.
      mutate((t) => t.filter((existing) => existing.id !== id));
      setUpdateError("Couldn't create that todo, so it was removed.");
    }
  };

  // `failMessage` is the banner shown if the change is rolled back; only the
  // repeat reset passes its own. Callers outside go through updateTodo.
  const saveTodo = async (todo: TodoData, updates: TodoUpdates, failMessage = SAVE_ERROR) => {
    // Locally, "cleared" is just absent (TodoData has no `null` variant for
    // these) — `null` is only meaningful as a wire signal to the API.
    // parentCreatedAt only travels to the API (see TodoUpdates), so it is
    // neither applied locally nor tracked as an in-flight field edit.
    const {
      scheduledDate,
      dueDate,
      priority,
      complexity,
      parentId,
      repeat,
      parentCreatedAt: _wireOnly,
      ...restUpdates
    } = updates;
    const localUpdates: Partial<TodoData> = { ...restUpdates };
    if ("scheduledDate" in updates) {
      localUpdates.scheduledDate = scheduledDate === null ? undefined : scheduledDate;
    }
    if ("parentId" in updates) {
      localUpdates.parentId = parentId === null ? undefined : parentId;
    }
    if ("dueDate" in updates) {
      localUpdates.dueDate = dueDate === null ? undefined : dueDate;
    }
    if ("priority" in updates) {
      localUpdates.priority = priority === null ? undefined : priority;
    }
    if ("complexity" in updates) {
      localUpdates.complexity = complexity === null ? undefined : complexity;
    }
    if ("repeat" in updates) {
      localUpdates.repeat = repeat === null ? undefined : repeat;
    }
    // Track each touched field's in-flight edits so overlapping edits settle
    // correctly whatever order they finish in: once none are left in flight,
    // the field ends at the value of the newest edit that succeeded, or at
    // its original value if none did.
    const previous = todosRef.current.find((existing) => existing.id === todo.id);
    const keys = Object.keys(localUpdates) as (keyof TodoData)[];
    const mine = keys.map((key) => {
      const mapKey = `${todo.id}:${key}`;
      let edit = fieldEditsRef.current.get(mapKey);
      if (!edit) {
        edit = { base: previous?.[key], inflight: 0, seq: 0, okSeq: 0, okValue: undefined };
        fieldEditsRef.current.set(mapKey, edit);
      }
      edit.inflight++;
      return { key, mapKey, edit, seq: ++edit.seq };
    });
    mutate((t) =>
      t.map((existing) =>
        existing.id === todo.id ? { ...existing, ...localUpdates } : existing,
      ),
    );
    let ok = false;
    try {
      const result = await TodoApi.updateTodo({
        id: todo.id,
        createdAt: todo.createdAt,
        ...updates,
      });
      ok = result?.success !== false;
    } catch {
      ok = false;
    }
    const revert: Partial<TodoData> = {};
    for (const { key, mapKey, edit, seq } of mine) {
      edit.inflight--;
      if (ok && seq > edit.okSeq) {
        edit.okSeq = seq;
        edit.okValue = localUpdates[key];
      }
      if (edit.inflight > 0) continue;
      fieldEditsRef.current.delete(mapKey);
      if (edit.okSeq !== edit.seq) {
        (revert as Record<string, unknown>)[key] = edit.okSeq ? edit.okValue : edit.base;
      }
    }
    // Only say "undone" if something visibly went back; a failed edit that a
    // newer edit already superseded has nothing to undo.
    const current = todosRef.current.find((existing) => existing.id === todo.id);
    const reverted =
      !!current &&
      (Object.keys(revert) as (keyof TodoData)[]).some((key) => current[key] !== revert[key]);
    if (reverted) {
      mutate((t) =>
        t.map((existing) =>
          existing.id === todo.id ? { ...existing, ...revert } : existing,
        ),
      );
      setUpdateError(failMessage);
    }
    return ok;
  };
  const updateTodo = (todo: TodoData, updates: TodoUpdates) => saveTodo(todo, updates);

  const deleteTodo = async (todo: TodoData) => {
    // Snapshot this todo and the pre-delete id order. Restoring places it
    // relative to its surviving successors in that order, so overlapping
    // failed deletes put each todo back where it was whatever order they fail in.
    const current = todosRef.current;
    const removedTodo = current.find((existing) => existing.id === todo.id);
    const removed = removedTodo
      ? { todo: removedTodo, ids: current.map((existing) => existing.id) }
      : undefined;
    mutate((t) => t.filter((existing) => existing.id !== todo.id));
    restoredRef.current.delete(todo.id);
    if (pendingLoadsRef.current > 0) tombstonesRef.current.add(todo.id);
    let ok = false;
    try {
      const result = await TodoApi.deleteTodo(todo.id, todo.createdAt);
      // 404 means it's already gone (e.g. a retry after a delete that did
      // succeed), which is the outcome we wanted.
      // Judge by HTTP status, not just the body: an error response may have
      // no `success` field at all.
      const status = result?.status ?? 0;
      ok = status === 404 || (status >= 200 && status < 300 && result?.success !== false);
    } catch {
      ok = false;
    }
    if (!ok) tombstonesRef.current.delete(todo.id);
    if (ok) {
      // Gone for good: drop its edit bookkeeping so no stale rollback applies.
      for (const mapKey of Array.from(fieldEditsRef.current.keys())) {
        if (mapKey.startsWith(`${todo.id}:`)) fieldEditsRef.current.delete(mapKey);
      }
    }
    if (!ok && removed) {
      const { todo: original, ids } = removed;
      mutate((t) => {
        if (t.some((existing) => existing.id === original.id)) return t;
        const later = new Set(ids.slice(ids.indexOf(original.id) + 1));
        const at = t.findIndex((existing) => later.has(existing.id));
        const next = [...t];
        next.splice(at === -1 ? next.length : at, 0, original);
        return next;
      });
      if (pendingLoadsRef.current > 0) restoredRef.current.add(original.id);
      setUpdateError("Couldn't delete that todo, so it was restored.");
    }
  };

  return (
    <dataContext.Provider value={{ todos, loading, createTodo, updateTodo, deleteTodo }}>
      {updateError && (
        <div className="max-w-2xl mx-auto mt-2 px-3 py-2 rounded text-sm flex justify-between bg-red-50 text-red-800">
          <span>{updateError}</span>
          <span className="flex gap-3">
            {updateError === LOAD_ERROR && (
              <button onClick={load} disabled={loading} className="text-xs underline disabled:opacity-50">
                {loading ? "Retrying…" : "Retry"}
              </button>
            )}
            <button
              onClick={() => {
                setUpdateError(null);
              }}
              className="text-xs"
            >
              dismiss
            </button>
          </span>
        </div>
      )}
      {children}
    </dataContext.Provider>
  );
};

export const useData = () => useContext(dataContext);
